// Dynamic Nameplate Culling & Batching
// Inspired by WoW's clean nameplate system that scales
// Priority-based visibility with batched DOM updates

import * as THREE from 'three';

export interface NameplateEntity {
  id: string;
  name: string;
  level?: number;
  health: number;
  maxHealth: number;
  position: THREE.Vector3;
  isHostile: boolean;
  isNPC: boolean;
  isTargeted: boolean;
  isDead: boolean;
}

interface NameplateElement {
  container: HTMLDivElement;
  nameText: HTMLSpanElement;
  levelText: HTMLSpanElement;
  healthBar: HTMLDivElement;
  healthFill: HTMLDivElement;
  visible: boolean;
  lastUpdateFrame: number;
}

interface NameplatePriority {
  entity: NameplateEntity;
  priority: number;
  screenX: number;
  screenY: number;
  distance: number;
  isOnScreen: boolean;
}

export class OptimizedNameplateSystem {
  private camera: THREE.Camera;
  private container: HTMLElement;
  private nameplates: Map<string, NameplateElement> = new Map();
  private entities: Map<string, NameplateEntity> = new Map();

  // Visibility limits
  private readonly MAX_VISIBLE_NAMEPLATES = 25;
  private readonly MAX_DISTANCE = 50;
  private readonly MIN_SCREEN_Y = 0;
  private readonly MAX_SCREEN_Y = 0.9;

  // Batching
  private pendingUpdates: Map<string, Partial<NameplateEntity>> = new Map();
  private frameCount = 0;
  private readonly UPDATE_BATCH_SIZE = 10;
  private readonly POSITION_UPDATE_INTERVAL = 2; // Update positions every N frames

  // Reusable objects
  private readonly _screenPos = new THREE.Vector3();
  private readonly _tempVec = new THREE.Vector3();
  private visibleList: NameplatePriority[] = [];

  // DOM pooling
  private elementPool: HTMLDivElement[] = [];
  private readonly POOL_SIZE = 30;

  constructor(camera: THREE.Camera, container: HTMLElement) {
    this.camera = camera;
    this.container = container;
    this.initializePool();
    this.createStyles();
  }

  private initializePool(): void {
    for (let i = 0; i < this.POOL_SIZE; i++) {
      const element = this.createNameplateElement();
      element.style.display = 'none';
      this.container.appendChild(element);
      this.elementPool.push(element);
    }
  }

  private createStyles(): void {
    const style = document.createElement('style');
    style.textContent = `
      .opt-nameplate {
        position: absolute;
        transform: translate(-50%, -100%);
        pointer-events: none;
        text-align: center;
        font-family: 'Segoe UI', sans-serif;
        font-size: 12px;
        will-change: transform, opacity;
        transition: opacity 0.15s ease-out;
      }
      .opt-nameplate-name {
        color: #fff;
        text-shadow: 1px 1px 2px #000, -1px -1px 2px #000;
        white-space: nowrap;
      }
      .opt-nameplate-level {
        color: #ffcc00;
        font-size: 10px;
        margin-left: 4px;
      }
      .opt-nameplate-bar-bg {
        width: 60px;
        height: 6px;
        background: #333;
        border: 1px solid #000;
        border-radius: 2px;
        margin: 2px auto 0;
        overflow: hidden;
      }
      .opt-nameplate-bar-fill {
        height: 100%;
        background: linear-gradient(to bottom, #00ff00, #00aa00);
        transition: width 0.1s ease-out;
      }
      .opt-nameplate-bar-fill.low {
        background: linear-gradient(to bottom, #ff0000, #aa0000);
      }
      .opt-nameplate-bar-fill.medium {
        background: linear-gradient(to bottom, #ffff00, #aaaa00);
      }
      .opt-nameplate.hostile .opt-nameplate-name {
        color: #ff4444;
      }
      .opt-nameplate.npc .opt-nameplate-name {
        color: #44ff44;
      }
      .opt-nameplate.targeted {
        z-index: 100;
      }
      .opt-nameplate.targeted .opt-nameplate-bar-bg {
        width: 80px;
        height: 8px;
        border-color: #fff;
      }
    `;
    document.head.appendChild(style);
  }

  private createNameplateElement(): HTMLDivElement {
    const container = document.createElement('div');
    container.className = 'opt-nameplate';

    const nameRow = document.createElement('div');
    const nameText = document.createElement('span');
    nameText.className = 'opt-nameplate-name';
    const levelText = document.createElement('span');
    levelText.className = 'opt-nameplate-level';
    nameRow.appendChild(nameText);
    nameRow.appendChild(levelText);

    const healthBarBg = document.createElement('div');
    healthBarBg.className = 'opt-nameplate-bar-bg';
    const healthFill = document.createElement('div');
    healthFill.className = 'opt-nameplate-bar-fill';
    healthBarBg.appendChild(healthFill);

    container.appendChild(nameRow);
    container.appendChild(healthBarBg);

    // Store references
    (container as unknown as Record<string, HTMLElement>)._nameText = nameText;
    (container as unknown as Record<string, HTMLElement>)._levelText = levelText;
    (container as unknown as Record<string, HTMLElement>)._healthFill = healthFill;

    return container;
  }

  private getPooledElement(): HTMLDivElement | null {
    return this.elementPool.pop() ?? null;
  }

  private returnToPool(element: HTMLDivElement): void {
    element.style.display = 'none';
    element.className = 'opt-nameplate';
    this.elementPool.push(element);
  }

  public addEntity(entity: NameplateEntity): void {
    this.entities.set(entity.id, entity);
  }

  public removeEntity(id: string): void {
    this.entities.delete(id);
    const nameplate = this.nameplates.get(id);
    if (nameplate) {
      this.returnToPool(nameplate.container);
      this.nameplates.delete(id);
    }
  }

  public updateEntity(id: string, updates: Partial<NameplateEntity>): void {
    const entity = this.entities.get(id);
    if (entity) {
      Object.assign(entity, updates);
      this.pendingUpdates.set(id, updates);
    }
  }

  public update(): void {
    this.frameCount++;

    // Calculate priorities and visibility
    this.calculateVisibility();

    // Apply pending updates in batches
    this.processPendingUpdates();

    // Update nameplate positions (throttled)
    if (this.frameCount % this.POSITION_UPDATE_INTERVAL === 0) {
      this.updatePositions();
    }
  }

  private calculateVisibility(): void {
    this.visibleList.length = 0;
    const cameraPos = this.camera.position;

    for (const entity of this.entities.values()) {
      if (entity.isDead) continue;

      // Calculate distance
      const distance = this._tempVec.copy(entity.position).distanceTo(cameraPos);
      if (distance > this.MAX_DISTANCE) continue;

      // Project to screen
      this._screenPos.copy(entity.position);
      this._screenPos.y += 2; // Offset above entity
      this._screenPos.project(this.camera);

      // Check if on screen
      const isOnScreen =
        this._screenPos.x >= -1 &&
        this._screenPos.x <= 1 &&
        this._screenPos.y >= -1 &&
        this._screenPos.y <= 1 &&
        this._screenPos.z < 1; // In front of camera

      if (!isOnScreen) continue;

      // Calculate priority
      let priority = 0;

      // Targeted entities have highest priority
      if (entity.isTargeted) priority += 1000;

      // Hostile entities are more important
      if (entity.isHostile) priority += 100;

      // Damaged entities are more important
      if (entity.health < entity.maxHealth) priority += 50;

      // NPCs with interaction
      if (entity.isNPC) priority += 25;

      // Closer entities are more important
      priority += (this.MAX_DISTANCE - distance) * 2;

      this.visibleList.push({
        entity,
        priority,
        screenX: (this._screenPos.x + 1) * 0.5 * window.innerWidth,
        screenY: (1 - this._screenPos.y) * 0.5 * window.innerHeight,
        distance,
        isOnScreen,
      });
    }

    // Sort by priority (highest first)
    this.visibleList.sort((a, b) => b.priority - a.priority);

    // Limit visible count
    const visibleIds = new Set<string>();
    const maxVisible = Math.min(this.visibleList.length, this.MAX_VISIBLE_NAMEPLATES);

    for (let i = 0; i < maxVisible; i++) {
      visibleIds.add(this.visibleList[i].entity.id);
    }

    // Hide nameplates that are no longer visible
    for (const [id, nameplate] of this.nameplates) {
      if (!visibleIds.has(id) && nameplate.visible) {
        nameplate.container.style.opacity = '0';
        nameplate.visible = false;
      }
    }

    // Show/create nameplates for visible entities
    for (let i = 0; i < maxVisible; i++) {
      const item = this.visibleList[i];
      this.ensureNameplate(item.entity);
    }
  }

  private ensureNameplate(entity: NameplateEntity): void {
    let nameplate = this.nameplates.get(entity.id);

    if (!nameplate) {
      const container = this.getPooledElement();
      if (!container) return; // Pool exhausted

      const refs = container as unknown as Record<string, HTMLElement>;
      nameplate = {
        container,
        nameText: refs._nameText as HTMLSpanElement,
        levelText: refs._levelText as HTMLSpanElement,
        healthBar: container.querySelector('.opt-nameplate-bar-bg') as HTMLDivElement,
        healthFill: refs._healthFill as HTMLDivElement,
        visible: false,
        lastUpdateFrame: 0,
      };

      this.nameplates.set(entity.id, nameplate);

      // Initialize content
      nameplate.nameText.textContent = entity.name;
      nameplate.levelText.textContent = entity.level ? `[${entity.level}]` : '';

      // Set classes
      if (entity.isHostile) nameplate.container.classList.add('hostile');
      if (entity.isNPC) nameplate.container.classList.add('npc');
    }

    // Show if hidden
    if (!nameplate.visible) {
      nameplate.container.style.display = 'block';
      nameplate.container.style.opacity = '1';
      nameplate.visible = true;
    }

    // Update targeted state
    nameplate.container.classList.toggle('targeted', entity.isTargeted);

    // Update health bar
    const healthPercent = entity.health / entity.maxHealth;
    nameplate.healthFill.style.width = `${healthPercent * 100}%`;
    nameplate.healthFill.classList.toggle('low', healthPercent <= 0.25);
    nameplate.healthFill.classList.toggle('medium', healthPercent > 0.25 && healthPercent <= 0.5);
  }

  private processPendingUpdates(): void {
    if (this.pendingUpdates.size === 0) return;

    let processed = 0;
    for (const [id, _updates] of this.pendingUpdates) {
      if (processed >= this.UPDATE_BATCH_SIZE) break;

      const entity = this.entities.get(id);
      const nameplate = this.nameplates.get(id);

      if (entity && nameplate) {
        // Update health bar
        const healthPercent = entity.health / entity.maxHealth;
        nameplate.healthFill.style.width = `${healthPercent * 100}%`;
        nameplate.healthFill.classList.toggle('low', healthPercent <= 0.25);
        nameplate.healthFill.classList.toggle('medium', healthPercent > 0.25 && healthPercent <= 0.5);
      }

      this.pendingUpdates.delete(id);
      processed++;
    }
  }

  private updatePositions(): void {
    const maxVisible = Math.min(this.visibleList.length, this.MAX_VISIBLE_NAMEPLATES);

    for (let i = 0; i < maxVisible; i++) {
      const item = this.visibleList[i];
      const nameplate = this.nameplates.get(item.entity.id);

      if (nameplate && nameplate.visible) {
        // Use transform for GPU acceleration
        nameplate.container.style.transform = `translate(${item.screenX}px, ${item.screenY}px) translate(-50%, -100%)`;

        // Fade based on distance
        const opacity = Math.max(0.3, 1 - item.distance / this.MAX_DISTANCE);
        nameplate.container.style.opacity = String(opacity);
      }
    }
  }

  public setTarget(id: string | null): void {
    for (const entity of this.entities.values()) {
      entity.isTargeted = entity.id === id;
    }
  }

  public destroy(): void {
    for (const nameplate of this.nameplates.values()) {
      this.container.removeChild(nameplate.container);
    }
    for (const element of this.elementPool) {
      this.container.removeChild(element);
    }
    this.nameplates.clear();
    this.entities.clear();
    this.elementPool.length = 0;
  }

  // Debug stats
  public getStats(): { visible: number; total: number; pooled: number } {
    let visible = 0;
    for (const nameplate of this.nameplates.values()) {
      if (nameplate.visible) visible++;
    }
    return {
      visible,
      total: this.entities.size,
      pooled: this.elementPool.length,
    };
  }
}
